import { ServerSideSort, SortState } from '@/components/tables/AxleTable';
import {
    useVisitFilterDialogFilters,
    useVisitRequestFilterDialogFilters,
} from '@/components/visits/VisitFilterDialog';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { AllVisitsContext } from '@/pages/AllVisitsPage/AllVisits.context';
import { AllVisitsPageNS } from '@/pages/AllVisitsPage/AllVisitsPage.types';
import {
    VIEW_FILTERS,
    ViewFilters,
} from '@/pages/VisitRequestsListPage/queues/VisitRequestQueueTabs';
import { createEffectiveDateInRangeFilter } from '@/types/visit';
import {
    Ordering,
    VisitFilter,
    VisitOrdering,
    VisitRequestFilter,
    VisitRequestOrdering,
} from 'gql-types';
import moment from 'moment';
import { FC, ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Maps a table column field name to a VisitRequestOrdering input.
 * Returns undefined if the field is not server-side sortable.
 */
function sortStateToVisitRequestOrdering(sort: SortState | null): VisitRequestOrdering | undefined {
    if (!sort) return undefined;
    const direction: Ordering = sort.direction === 'asc' ? Ordering.ASC : Ordering.DESC;
    switch (sort.sortedByField) {
        case 'sortDate':
            return { tentativeDate: direction };
        case 'patient.fullName':
            return { patient: { fullName: direction } };
        case 'fulfillmentWindow.startDate':
            return { fulfillmentWindowStartDate: direction };
        default:
            return undefined;
    }
}

/**
 * Maps a table column field name to a VisitOrdering input.
 * Returns undefined if the field is not server-side sortable.
 */
function sortStateToVisitOrdering(sort: SortState | null): VisitOrdering | undefined {
    if (!sort) return undefined;
    const direction: Ordering = sort.direction === 'asc' ? Ordering.ASC : Ordering.DESC;
    switch (sort.sortedByField) {
        case 'sortDate':
            return { tentativeDate: direction };
        case 'patient.fullName':
            return { patient: { fullName: direction } };
        default:
            return undefined;
    }
}

interface IBulkTableContext {
    visitRequestFilters: VisitRequestFilter | 'exclude';
    visitFilters: VisitFilter | 'exclude';
    visitRequestOrdering: VisitRequestOrdering | undefined;
    visitOrdering: VisitOrdering | undefined;
    serverSideSort: ServerSideSort;
}

const BulkTableContext = createContext<IBulkTableContext | null>(null);

type BulkTableContextProviderProps = {
    children: ReactNode;
    view: AllVisitsPageNS.PrimaryTab;
};

type UseBulkViewFiltersResult = {
    visitRequestFilters: VisitRequestFilter | 'exclude';
    visitFilters: VisitFilter | 'exclude';
};

type UseBulkViewFiltersOptions = {
    view?: AllVisitsPageNS.PrimaryTab;
    startDate?: Date;
    endDate?: Date;
};

const EMPTY_FILTER: ViewFilters = {
    visitRequest: {},
    visit: {},
};

type UseBulkViewFilters = (options: UseBulkViewFiltersOptions) => UseBulkViewFiltersResult;

export const useBulkViewFilters: UseBulkViewFilters = (options) => {
    const { view } = options;
    const {
        dateRange: [[start, end]],
        search: [, , searchTerm],
    } = useContext(AllVisitsContext);

    const dialogFilters = useVisitRequestFilterDialogFilters();
    const visitDialogFilters = useVisitFilterDialogFilters();

    const FILTERS = view ? VIEW_FILTERS[view] : EMPTY_FILTER;

    const visitRequestFilters = useMemo(
        (): VisitRequestFilter | 'exclude' =>
            FILTERS.visitRequest === 'exclude'
                ? 'exclude'
                : {
                      ...dialogFilters,
                      ...FILTERS.visitRequest,
                      patient: { searchTerm },
                      AND: {
                          tentativeDate: {
                              range: {
                                  start: GraphInputHelpers.date(start),
                                  end: GraphInputHelpers.date(moment(end).add(1, 'day')),
                              },
                          },
                          OR: {
                              tentativeDate: {
                                  isNull: true,
                              },
                              fulfillmentWindowStartDate: {
                                  lte: GraphInputHelpers.date(end),
                              },
                              fulfillmentWindowEndDate: {
                                  gte: GraphInputHelpers.date(start),
                              },
                          },
                      },
                  },
        [FILTERS.visitRequest, dialogFilters, start, end, searchTerm],
    );

    const visitFilters = useMemo(
        (): VisitFilter | 'exclude' =>
            FILTERS.visit === 'exclude'
                ? 'exclude'
                : {
                      ...visitDialogFilters,
                      ...FILTERS.visit,
                      patient: { searchTerm },
                      AND: createEffectiveDateInRangeFilter(start, end),
                  },
        [FILTERS.visit, visitDialogFilters, start, end, searchTerm],
    );

    return {
        visitRequestFilters,
        visitFilters,
    };
};

export const BulkTableContextProvider: FC<BulkTableContextProviderProps> = (props) => {
    const { children, view } = props;
    const { visitRequestFilters, visitFilters } = useBulkViewFilters({ view });
    const [sort, setSort] = useState<SortState | null>(null);

    const visitRequestOrdering = useMemo(() => sortStateToVisitRequestOrdering(sort), [sort]);
    const visitOrdering = useMemo(() => sortStateToVisitOrdering(sort), [sort]);

    const onSort = useCallback((state: SortState | null) => setSort(state), []);
    const serverSideSort = useMemo((): ServerSideSort => [sort, onSort], [sort, onSort]);

    const value = useMemo(
        () => ({
            visitRequestFilters,
            visitFilters,
            visitRequestOrdering,
            visitOrdering,
            serverSideSort,
        }),
        [visitRequestFilters, visitFilters, visitRequestOrdering, visitOrdering, serverSideSort],
    );

    return <BulkTableContext.Provider value={value}>{children}</BulkTableContext.Provider>;
};

export const useBulkTableContext = () => {
    const context = useContext(BulkTableContext);
    if (!context) {
        throw new Error('useBulkTableContext must be used within a BulkTableContextProvider');
    }
    return context;
};
